import { useState, useEffect, useRef, useCallback, useMemo, ChangeEvent } from "react";
import _ from "lodash";
import {
  Box,
  Tab,
  List,
  Tabs,
  Paper,
  Stack,
  Button,
  Popper,
  Divider,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  ListItemText,
  ListItemButton,
  CircularProgress,
  ClickAwayListener
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { AlgoliaResultType } from "./algoliaClient";
import Label from "components/Label";
import { ProcessedListingAddress } from "./../../../@types/sharedSchema";
import type { LocationGroup } from "./../../../@types/sharedSchema";
import { getListingAddressString } from "utils/Haim/utils";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack5";
import { useModal } from "hooks/useModal";
import useFirestore from "hooks/useFirestore";
import useLocationGroups from "hooks/useLocationGroups";
import { AutocompleteType, CreateAddressDetails, CreateAddressDialog } from "./CreateAddressDialog";
import { Add, Close, Edit, Layers, Settings, StarBorder } from "@material-ui/icons";
import useWebotServer from "hooks/useWebotServer";
import { preventBubbling } from "utils/listings/helper";
import { LocationGroupChip } from "../locationGroups/LocationGroupChip";
// This closes a module cycle — the manager hosts the group EDITOR, which hosts a
// LocationPicker of its own (with `hideQuickTabs`). It is safe because every member
// of the cycle is an `export function` declaration, so the binding is hoisted and
// already defined even in a partially-evaluated module, and none of them touches
// another at module-evaluation time. Keep it that way: converting any of them to a
// `const` arrow export would turn this into a TDZ crash on first import.
import { LocationGroupsManagerDialog } from "../locationGroups/LocationGroupsManagerDialog";
import {
  buildSelectionChips,
  locationIdentityKey,
  mergeGroupIntoSelection,
  removeSelectionChip,
  summarizeGroupLocations
} from "utils/locations/locationGroups";
import type { SelectionChip } from "utils/locations/locationGroups";
import type { ListingInteractiveMapBaseProps } from "../listings/modal/core/ListingInteractiveMap";

// Debounce function to delay API calls
const useDebouncedEffect = (effect, delay, deps) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const callback = useCallback(effect, deps);

  useEffect(() => {
    const handler = setTimeout(() => {
      callback();
    }, delay);

    return () => clearTimeout(handler);
  }, [callback, delay]);
};

/**
 * One suggestion row: the location a pick MEANS, plus display-only metadata that
 * must never ride along into the selection.
 *
 * Keeping them in two fields is the enforcement, not a convention: every pick
 * path is handed `row.location`, so there is no shape in which `hoodNames` can
 * become part of what was picked. A street suggestion is city + street on the
 * default wire, and a `neighborhood` appearing on one silently narrows every
 * raw-fallback search filter into a street∧neighborhood conjunction and changes
 * what an alert picker persists (see `docs/rules/location-ssot.md`).
 */
type SuggestionRow = {
  location: ProcessedListingAddress;
  hoodNames?: string[];
  hoodCount?: number;
};

/** Which pre-typing tab the realtor last used. Per-browser, best-effort. */
const QUICK_TAB_STORAGE_KEY = "webot:locationPicker:quickTab";
const QUICK_TABS = ["favorites", "groups"] as const;
type QuickTab = typeof QUICK_TABS[number];
const DEFAULT_QUICK_TAB: QuickTab = "favorites";

/**
 * Read-with-a-whitelist rather than read-with-a-default: a stored value from an
 * older build (or a hand-edited one) must land on a real tab, because an
 * unrecognised `value` on MUI's `Tabs` renders the bar with nothing selected AND
 * a body that matches no branch — an empty panel that looks like a dead feature.
 */
function readStoredQuickTab(): QuickTab {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(QUICK_TAB_STORAGE_KEY) : null;
    return QUICK_TABS.includes(raw as QuickTab) ? (raw as QuickTab) : DEFAULT_QUICK_TAB;
  } catch {
    return DEFAULT_QUICK_TAB;
  }
}

function writeStoredQuickTab(tab: QuickTab): void {
  try {
    window.localStorage.setItem(QUICK_TAB_STORAGE_KEY, tab);
  } catch {
    /* ignore quota / private-mode write errors — persistence is best-effort */
  }
}

/** What a merge did, in the counts the copy needs to tell apart. */
type MergeOutcome = {
  locations: ProcessedListingAddress[];
  added: number;
  /** Loose picks this group adopted — a success, never a duplicate to warn about. */
  absorbed: number;
  skippedDuplicates: number;
  skippedOverLimit: number;
};

/** Row styling shared by every pickable row in the dropdown, quick panel included. */
const rowStyle = (selected: boolean) =>
  selected
    ? { backgroundColor: "#f0f0f0", color: "#0a6151", cursor: "not-allowed" as const }
    : { color: "#0a6151" };

const sectionHeaderStyle = { padding: "8px 16px", backgroundColor: "#d1ffe4", color: "#0a6151" };

type QuickPanelProps = {
  tab: QuickTab;
  onTabChange: (tab: QuickTab) => void;
  favorites: ProcessedListingAddress[];
  groups: LocationGroup[];
  groupsLoading: boolean;
  isHittingLimit: boolean;
  isLocationPicked: (location: ProcessedListingAddress) => boolean;
  onPickLocation: (location: ProcessedListingAddress) => void;
  onAddAllFavorites: () => void;
  onPickGroup: (group: LocationGroup) => void;
  onOpenManager: () => void;
};

/**
 * The pre-typing panel: the realtor's own activity areas and their saved groups,
 * one tap each, before there is anything to search for.
 *
 * It owns its own scroll region rather than riding the `Paper`'s, so the tab bar
 * and the manage cog stay pinned — the `Paper` is capped at 300px and a 40-group
 * list would otherwise scroll them out of reach.
 *
 * Declared at module level, not inside `LocationPicker`: a component defined
 * inside a render is a new type on every render, so React would unmount and
 * remount the whole panel on every keystroke, losing scroll position.
 */
function QuickPanel({
  tab,
  onTabChange,
  favorites,
  groups,
  groupsLoading,
  isHittingLimit,
  isLocationPicked,
  onPickLocation,
  onAddAllFavorites,
  onPickGroup,
  onOpenManager
}: QuickPanelProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();

  const everyFavoritePicked = favorites.length > 0 && favorites.every(isLocationPicked);

  const renderFavorites = () => {
    if (!favorites.length) {
      return (
        <Box sx={{ px: 2, py: 2.5 }}>
          <Typography variant="subtitle2">{t("listingsMatch.picker.favoritesEmpty")}</Typography>
          {/* Deliberately a sentence naming Settings rather than a link: this picker
              lives inside dialogs (the contact modal) and CRM cell editors, where
              navigating away would discard whatever the realtor was editing. */}
          <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "grey.600" }}>
            {t("listingsMatch.picker.favoritesHint")}
          </Typography>
          {/* A realtor with no saved areas is looking at a dead panel otherwise, and
              the other half of it is something they can act on right now. */}
          <Button
            size="small"
            startIcon={<Layers />}
            onClick={onOpenManager}
            sx={{ mt: 1, fontSize: 12 }}
          >
            {t("listingsMatch.picker.manageGroups")}
          </Button>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ px: 1, pt: 0.5 }}>
          <Button
            size="small"
            startIcon={<Add />}
            disabled={isHittingLimit || everyFavoritePicked}
            onClick={onAddAllFavorites}
            sx={{ fontSize: 12 }}
          >
            {t("listingsMatch.picker.addAllFavorites")}
          </Button>
        </Box>
        <List dense>
          {favorites.map((favorite, index) => {
            const selected = isLocationPicked(favorite);

            return (
              <ListItemButton
                key={`${locationIdentityKey(favorite)}-${index}`}
                disabled={isHittingLimit || selected}
                onClick={() => !selected && onPickLocation(favorite)}
                style={rowStyle(selected)}
              >
                <ListItemText primary={getListingAddressString(favorite)} />
              </ListItemButton>
            );
          })}
        </List>
      </>
    );
  };

  const renderGroups = () => {
    if (groupsLoading && !groups.length) {
      // A real spinner, and the reserved height is the same as the list it will be
      // replaced by, so nothing jumps.
      //
      // This used to be a blank box, on the theory that the snapshot resolves
      // within a frame and any placeholder would flash. That theory was wrong —
      // this app runs Firestore with no offline persistence, so a cold listen is a
      // full round trip — and the realtor got an empty panel with no explanation
      // for as long as it took. `loading` is now true ONLY when there is genuinely
      // nothing cached, so a warm open still renders the list with no spinner at
      // all; when this does show, waiting is the honest thing to say.
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 96 }}>
          <CircularProgress size={22} />
        </Stack>
      );
    }

    if (!groups.length) {
      return (
        <Box sx={{ px: 2, py: 2.5 }}>
          <Typography variant="subtitle2">{t("listingsMatch.picker.groupsEmpty")}</Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "grey.600" }}>
            {t("listingsMatch.picker.groupsEmptyHint")}
          </Typography>
          <Button size="small" startIcon={<Add />} onClick={onOpenManager} sx={{ mt: 1, fontSize: 12 }}>
            {t("locationGroups.createFirst")}
          </Button>
        </Box>
      );
    }

    return (
      <List dense>
        {groups.map((group) => {
          const count = group.locations?.length ?? 0;

          return (
            <ListItemButton
              key={group.id}
              disabled={isHittingLimit}
              onClick={() => onPickGroup(group)}
              sx={{ alignItems: "flex-start" }}
            >
              <ListItemText
                // `component: "div"`: ListItemText wraps `primary` in a Typography
                // span, and the row below is a flex container.
                primaryTypographyProps={{ component: "div" }}
                primary={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    {group.emoji ? (
                      <Box component="span" sx={{ fontSize: 15, lineHeight: 1 }}>
                        {group.emoji}
                      </Box>
                    ) : (
                      <Layers sx={{ width: 16, height: 16, color: "warning.dark" }} />
                    )}
                    <Box
                      component="span"
                      sx={{
                        minWidth: 0,
                        fontWeight: 600,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {group.name}
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        px: 0.625,
                        py: "1px",
                        flex: "none",
                        fontSize: 11,
                        fontWeight: 700,
                        borderRadius: "6px",
                        // Fixed `darker`, NOT the mode-aware pair `LocationGroupChip`
                        // uses. That chip sits on `background.paper`, which flips with
                        // the theme; this badge sits inside the dropdown `Paper`, which
                        // is forced white in both — so dark ink is right here always.
                        color: theme.palette.warning.darker,
                        backgroundColor: alpha(theme.palette.warning.main, 0.32)
                      }}
                    >
                      {count}
                    </Box>
                  </Stack>
                }
                secondary={summarizeGroupLocations(group)}
                secondaryTypographyProps={{
                  variant: "caption",
                  noWrap: true,
                  sx: { color: "grey.600" }
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    );
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          flex: "none",
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "divider"
        }}
      >
        <Tabs
          value={tab}
          onChange={(_e, value: QuickTab) => onTabChange(value)}
          // The unselected label and the cog take their colour from `text.secondary`
          // / `action.active` — both of which are tuned for a DARK surface in dark
          // mode, and this dropdown is forced white in both. Named greys instead, so
          // they read the same in either theme. (Selected is `primary.main`, a fixed
          // green, and needs nothing.)
          sx={{
            flex: 1,
            minHeight: 40,
            "& .MuiTab-root:not(.Mui-selected)": { color: "grey.600" }
          }}
        >
          <Tab
            value="favorites"
            sx={{ minHeight: 40, minWidth: 0, px: 1.5, fontSize: 13 }}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <StarBorder sx={{ width: 15, height: 15 }} />
                <span>{t("listingsMatch.picker.tabFavorites")}</span>
              </Stack>
            }
          />
          <Tab
            value="groups"
            sx={{ minHeight: 40, minWidth: 0, px: 1.5, fontSize: 13 }}
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Layers sx={{ width: 15, height: 15 }} />
                <span>{t("listingsMatch.picker.tabGroups")}</span>
              </Stack>
            }
          />
        </Tabs>
        <Tooltip title={t("listingsMatch.picker.manageGroups")}>
          <IconButton
            size="small"
            onClick={onOpenManager}
            sx={{ mx: 0.5, flex: "none", color: "grey.600" }}
          >
            <Settings fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {tab === "groups" ? renderGroups() : renderFavorites()}
      </Box>
    </Box>
  );
}

type InlineLocationPicker = {
  maxPicks: 1;
  allowEditResults?: boolean;
};

type NormalLocationPicker = {
  // assume 999 means no limit. 200 is MAX_INTERESTED_IN_LOCATIONS — the cap every
  // picker that edits a contact's areas passes, kept in the union so the constant
  // (a `200` literal type) is assignable.
  maxPicks: 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 15 | 20 | 50 | 200 | 999;
};

type LocationPickerPropsBase = InlineLocationPicker | NormalLocationPicker;

export type LocationPickerProps = LocationPickerPropsBase & {
  small?: boolean;
  loading?: boolean;
  required?: boolean;
  disabled?: boolean;
  hideLabel?: boolean;
  pickedLocations?: ProcessedListingAddress[];
  autocompleteType?: AutocompleteType;
  algoliaResultType?: AlgoliaResultType;
  includeAddressCoords?: boolean;
  promptToCreateAddress?: boolean;
  restrictPickingCities?: boolean;
  showOnlyStreetResults?: boolean;
  /** Areas mode: only offer city + neighborhood results (no streets). Used by the
   *  contact modal, where "interested in" means areas, not exact addresses. */
  hideStreetResults?: boolean;
  /**
   * Whether suggestions may include places that currently hold no listings.
   * Defaults to true — alert/areas pickers (interested-in locations, settings,
   * lead areas, new-listing addresses) legitimately pick a place with no
   * listings today, so a future listing there can match. Listing-SEARCH pickers
   * must pass false: an empty place is a dead option there — picking it yields
   * zero results and reads as a bug.
   */
  includeEmpty?: boolean;
  listingInteractiveMapBaseProps?: ListingInteractiveMapBaseProps;
  removeNeighborhoodForStreetMatches?: boolean;
  /**
   * Optional node rendered inside the input's trailing adornment, before
   * the edit/clear/loading icons. Use this to slot a small inline action
   * (e.g. "use my activity areas") directly into the picker instead of as
   * a separate sibling button. Stops click propagation so the picker's
   * dropdown doesn't open on action clicks.
   */
  inlineAction?: React.ReactNode;
  /**
   * Suppress the pre-typing quick panel (⭐ my areas / 🗂️ my groups) and the
   * manage-groups cog.
   *
   * The panel is otherwise DERIVED, not configured: it appears whenever
   * `maxPicks > 1`, because a saved group only means anything on a picker that
   * can hold more than one location. The single exact-address picker
   * (`maxPicks === 1`) therefore never shows it and needs no opt-out.
   *
   * The one caller that does need this is the group EDITOR's own picker — without
   * it, editing a group would offer you groups to put inside it.
   */
  hideQuickTabs?: boolean;
  /**
   * A search handed in by the parent: the text lands in the input, the dropdown opens on
   * it and the input takes focus, exactly as if the realtor had typed it. Used by the
   * contact dialog's "could not place this" chips (a call named an area the registry did
   * not recognise). `nonce` changes on every hand-off so the same text can be re-seeded.
   */
  searchSeed?: { text: string; nonce: number };
  onLocationsChange: (locations: ProcessedListingAddress[]) => void;
};

export function LocationPicker(props: LocationPickerProps) {
  const {
    small,
    loading,
    required,
    maxPicks,
    disabled,
    hideLabel,
    pickedLocations,
    autocompleteType = "place",
    algoliaResultType = "any",
    includeAddressCoords,
    promptToCreateAddress,
    restrictPickingCities,
    hideStreetResults,
    includeEmpty = true,
    listingInteractiveMapBaseProps,
    // removeNeighborhoodForStreetMatches: retained on the props type for call-site
    // compatibility, but no longer consumed — the canonical endpoint already returns
    // street suggestions as city + street (no neighborhood), per the wire contract.
    inlineAction,
    hideQuickTabs,
    searchSeed,
    onLocationsChange
  } = props;

  const [query, setQuery] = useState("");
  const api = useWebotServer();
  const [suggestions, setSuggestions] = useState<SuggestionRow[]>([]);
  const [anchorEl, setAnchorEl] = useState(null); // For Popper positioning

  const inputRef = useRef(null); // Ref for the TextField
  /**
   * The field AND the picked areas under it: the list opens below both.
   *
   * Anchored to the field alone, the list covered the chip row, and a multi-pick picker stays
   * open after a pick, so the area the realtor had just taken appeared UNDER the list and all
   * they saw was the row turning grey. Reported from production 2026-11-02 as "the area search
   * is stuck"; the pick had worked and was saved.
   */
  const fieldRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { userSettings } = useFirestore();

  // The realtor's OWN activity areas — the `interestedInLocations` on their user
  // document, written from RealtorSettings. Not a contact's.
  const myAreas = userSettings?.interestedInLocations ?? [];

  // Unconditional on purpose: the chip row needs the LIVE groups even on a picker
  // whose quick panel is hidden, so a renamed group renames its chip everywhere.
  const { groups, loading: groupsLoading } = useLocationGroups();

  const [locations, setLocations] = useState<ProcessedListingAddress[]>(pickedLocations || []);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isEmptySuggestion, setIsEmptySuggestion] = useState(false);
  const [quickTab, setQuickTab] = useState<QuickTab>(readStoredQuickTab);

  const [randomNumber, setRandomNumber] = useState(0);

  const { open, openModal, closeModal } = useModal();
  const {
    open: groupsManagerOpen,
    openModal: openGroupsManagerModal,
    closeModal: closeGroupsManagerModal
  } = useModal();
  /** Set only when the manager was opened by a click on a group chip. */
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  const usingInlinePicker = maxPicks === 1;

  const isHittingLimit =
    typeof maxPicks === "number" ? !usingInlinePicker && locations.length >= maxPicks : false;

  const hasMultipleOptionsEnabled = maxPicks === undefined || maxPicks > 1;

  // update from external state, to allow parent to control the selected locations. We also have an internal state for the selected locations, to make it snappier
  //
  // An equal list in a new array is NOT a change, so it is not adopted. It used to be: a host
  // that hands down two equal lists in successive renders (the listings-match page does on every
  // buyer pick: the picker's copy of the card, then the live card) set this copy and the host's
  // chasing each other for good, each adopting the other's array and the effect below reporting
  // it back. The page redrew itself ~150 times a second until it was reloaded, and opening the
  // filters on top of that froze it (2026-11-04, a realtor's telemetry;
  // `__tests__/LocationPicker.sync.test.tsx` reproduces it). Only this direction compares: the
  // report below still runs on mount and on every change, as every host has always had it.
  useEffect(() => {
    setLocations((current) => (_.isEqual(current, pickedLocations) ? current : pickedLocations));
  }, [pickedLocations]);

  useEffect(() => {
    onLocationsChange?.(locations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  // A seeded search behaves like typing: same query state, same dropdown anchor, and the
  // real <input> (the ref sits on the TextField root) takes focus so the realtor can edit
  // the text straight away — the seed is usually a spelling the registry did not know.
  useEffect(() => {
    if (!searchSeed?.text) return;
    setQuery(searchSeed.text);
    setAnchorEl(fieldRef.current);
    const root = inputRef.current as HTMLElement | null;
    root?.querySelector("input")?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSeed?.nonce]);

  useEffect(() => {
    if (!usingInlinePicker) return;

    const timeoutId = setTimeout(() => {
      setQuery(getListingAddressString(locations[0]));
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [usingInlinePicker, randomNumber, locations]);

  /**
   * Each fetch takes a number, and only the newest may write. A server instance still loading
   * its registry answered in 6–14 s, and then every keystroke's request landed at once in any
   * order, so the list could show what the realtor had typed three letters earlier.
   */
  const latestFetchRef = useRef(0);

  const fetchSuggestions = async () => {
    const fetchId = ++latestFetchRef.current;

    if (query.length < 2) {
      setSuggestions([]);
      // A fetch still running no longer owns the spinner.
      setIsLoadingSuggestions(false);
      return;
    }

    try {
      setIsLoadingSuggestions(true);
      setIsEmptySuggestion(false);

      // Suggestions now come from our canonical location registry (server) rather
      // than Algolia. The wire shape is compatible: each suggestion carries
      // city / neighborhood? / street? (absent fields absent) plus ids we ignore.
      // includeEmpty is per-context (see the prop doc): true for alert-areas
      // pickers, false for listing-search pickers where an empty place is a
      // dead option.
      const { suggestions: raw } = await api.locationsAutocomplete(query, {
        resultType: algoliaResultType,
        includeEmpty,
        includeCoords: includeAddressCoords
      });

      if (fetchId !== latestFetchRef.current) return;

      const results: SuggestionRow[] = raw.map((s) => ({
        location: {
          ...(s.city ? { city: s.city } : {}),
          ...(s.neighborhood ? { neighborhood: s.neighborhood } : {}),
          ...(s.street ? { street: s.street } : {}),
          ...(s.coords ? { coords: s.coords } : {}),
          // Carry the canonical master id so downstream search/alerts can match by id
          // (recovering listings written with a different source spelling for this master).
          ...(s.locationId ? { canonicalLocationId: s.locationId, canonicalLevel: s.level } : {})
        } as ProcessedListingAddress,
        // Display-only, and kept OUT of `location` for that reason — see SuggestionRow.
        ...(s.hoodNames?.length ? { hoodNames: s.hoodNames } : {}),
        ...(typeof s.hoodCount === "number" ? { hoodCount: s.hoodCount } : {})
      }));

      // Emptiness is decided on what this picker can actually RENDER, not on what
      // the server returned. `hideStreetResults` drops street rows below, so a
      // street-only answer used to leave this false while every list rendered
      // empty — and the picker then showed "type to search" at someone who was
      // already typing, with no way out and no support prompt. Reported from
      // production 2026-09-02: חורגין / חבצלת are streets in רמת גן, the server
      // returned them, and the field looked like it had simply hung.
      const renderable = hideStreetResults ? results.filter((row) => !row.location.street) : results;
      setIsEmptySuggestion(!renderable.length);

      setSuggestions(results);
    } catch (err) {
      console.error(err);
    } finally {
      if (fetchId === latestFetchRef.current) setIsLoadingSuggestions(false);
    }
  };

  useDebouncedEffect(
    () => {
      fetchSuggestions();
    },
    500,
    // includeEmpty is a dep so a context flip (e.g. listings-match picking a
    // contact) refetches instead of re-showing a list filtered the old way.
    [query, includeEmpty]
  ); // 500ms debounce on the typed query.

  /**
   * The identity of everything currently picked.
   *
   * `isLocationSelected` below stays as it is — a per-field containment test that
   * marks a city row as taken once any street in it is picked — because that is
   * the shipped behaviour of the suggestion list. This set is the STRICT test, and
   * it is what the quick panel and every merge use, so a row that reads "already
   * picked" is exactly a row a merge would count as a duplicate.
   */
  const pickedKeys = useMemo(
    () => new Set((locations ?? []).map(locationIdentityKey)),
    [locations]
  );

  const isLocationPicked = useCallback(
    (location: ProcessedListingAddress): boolean => pickedKeys.has(locationIdentityKey(location)),
    [pickedKeys]
  );

  /**
   * Pick ONE place.
   *
   * A multi-pick picker stays open and keeps its query, so the realtor can take
   * several results out of one search — "רמת" then רמת גן AND רמת השרון — and
   * several areas out of the ⭐/🗂️ panel, without re-opening the dropdown between
   * every tap. The row they just took re-renders as selected, so the list itself
   * says what has already been taken. Closing after the first pick is right only
   * for the single-address picker, where there is nothing left to choose.
   */
  const handleSelectLocation = (location: ProcessedListingAddress): void => {
    const shouldSetLocation = !isHittingLimit && !isLocationPicked(location);

    if (shouldSetLocation) {
      setLocations(hasMultipleOptionsEnabled ? [...locations, location] : [location]);
    }

    if (usingInlinePicker) {
      setQuery(getListingAddressString(location));
    }

    if (!hasMultipleOptionsEnabled) setAnchorEl(null);
  };

  const handleQueryChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
    setAnchorEl(fieldRef.current);
  };

  const handleBlurInput = (): void => {
    const shouldApplyInlineValue = usingInlinePicker && locations.length;

    if (!shouldApplyInlineValue) return;

    const random = Math.random();

    setRandomNumber(random);
  };

  /**
   * Delete what one chip stands for.
   *
   * This replaces a filter on object REFERENCE, which could not delete a chip
   * whose location had been through a JSON round-trip — a search state restored
   * from the URL, a contact document read back from Firestore — and group
   * expansion makes two equal-but-distinct objects the normal case, not the edge.
   * `removeSelectionChip` owns the rule: a group chip clears every member it
   * stamped, a location chip clears only the ungrouped pick naming that place.
   */
  const handleDeleteChip = (chip: SelectionChip) => (): void => {
    setLocations(removeSelectionChip(locations, chip, groups));
  };

  const handleClearLocation = () => {
    setQuery("");
    setLocations([]);
  };

  /**
   * Clears the typed text WITHOUT touching the selection, and keeps the dropdown
   * open so it falls back to the ⭐/🗂️ panel. `handleClearLocation` above is the
   * single-pick picker's ✕, where the text IS the selection — here they are two
   * different things and must not share a button.
   */
  const handleClearQuery = (): void => {
    setQuery("");
    setAnchorEl(fieldRef.current);
  };

  const handleConfirm = (data: CreateAddressDetails): void => {
    if (isHittingLimit) return;

    const processedData = {
      city: data.city,
      street: data.address,
      neighborhood: data.neighborhood
    };

    let parsedLocations = [processedData];

    if (hasMultipleOptionsEnabled) {
      parsedLocations = [...locations, processedData];
    }

    setLocations(parsedLocations);

    if (usingInlinePicker) {
      setQuery(getListingAddressString(processedData));
    } else {
      setQuery("");
    }

    setAnchorEl(null);
  };

  const handleClearInput = (): void => {
    setQuery("");
    setAnchorEl(null);
  };

  /**
   * A click on the field, or on the picked areas under it, is not "away". It used to be:
   * clicking back into the field with the list open closed the list and erased the typed text,
   * and the next click opened it again, so the field flipped open and shut under the cursor.
   */
  const handleClickAway = (event: MouseEvent | TouchEvent): void => {
    if (fieldRef.current?.contains(event.target as Node)) return;
    handleClearInput();
  };

  const isListOpen = Boolean(anchorEl);

  /**
   * Escape closes the LIST, and only the list.
   *
   * The picker never handled the key, so it went to whatever held the picker: in the contact
   * dialog that was the dialog's own close, and a realtor dismissing the list was asked to
   * discard the whole contact (2026-11-02). Taken at the document's capture phase, before
   * React's handlers see it, because focus is not reliably in the field: a tap on a row moves
   * it into the list, and the dialog's focus trap then pulls it out to the dialog itself.
   * While the list is closed, Escape belongs to whoever held it before.
   */
  useEffect(() => {
    if (!isListOpen) return undefined;

    const closeListOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      setAnchorEl(null);
    };

    document.addEventListener("keydown", closeListOnEscape, true);
    return () => document.removeEventListener("keydown", closeListOnEscape, true);
  }, [isListOpen]);

  const isLocationSelected = (location: ProcessedListingAddress): boolean => {
    return locations.some(
      (selected) =>
        (!location.city || selected.city === location.city) &&
        (!location.neighborhood || selected.neighborhood === location.neighborhood) &&
        (!location.street || selected.street === location.street)
    );
  };

  const openAddressDialog = (): void => {
    setAnchorEl(null);
    openModal();
  };

  /**
   * The popper sits at `zIndex: 1300`, which is MUI's own default modal level, so
   * it must be closed BEFORE the manager opens or it renders through the dialog.
   */
  const openGroupsManager = (): void => {
    setAnchorEl(null);
    setEditingGroupId(null);
    openGroupsManagerModal();
  };

  /** A click on a group CHIP edits THAT group — the manager opens on its editor. */
  const openGroupEditor = (groupId: string): void => {
    setAnchorEl(null);
    setEditingGroupId(groupId);
    openGroupsManagerModal();
  };

  /**
   * Say what a merge could NOT do — and only that.
   *
   * Success is already on screen: the chip appears, carrying the group's name and
   * its count. A toast saying "2 אזורים נוספו" over the top of it narrates
   * something the realtor is looking at, and it fires on the most common action
   * in the feature. What genuinely needs words is the silent half — areas asked
   * for that did not land, which nothing on screen would otherwise show.
   */
  const reportMergeOutcome = (outcome: MergeOutcome): void => {
    if (outcome.skippedOverLimit > 0) {
      enqueueSnackbar(t("locationGroups.limitReached", { max: maxPicks }), { variant: "warning" });
      return;
    }

    // Only worth saying when nothing landed at all: with some areas added, the new
    // chip already accounts for the tap.
    if (outcome.added === 0 && outcome.absorbed === 0 && outcome.skippedDuplicates > 0) {
      enqueueSnackbar(t("locationGroups.allAlreadyPicked"), { variant: "info" });
    }
  };

  /**
   * Add plain locations (the ⭐ tab's "add all"), with the same three counts a
   * group merge reports.
   *
   * Deliberately NOT `mergeGroupIntoSelection` with a synthetic group: that stamps
   * `fromGroupId` on every member, and the realtor's own activity areas are plain
   * picks — they would collapse into one group chip naming a group that does not
   * exist.
   */
  const mergePlainLocations = (incoming: ProcessedListingAddress[]): MergeOutcome => {
    const base = locations ?? [];
    const seen = new Set(base.map(locationIdentityKey));

    const fresh: ProcessedListingAddress[] = [];
    let skippedDuplicates = 0;

    for (const location of incoming ?? []) {
      const key = locationIdentityKey(location);
      if (seen.has(key)) {
        skippedDuplicates += 1;
        continue;
      }
      seen.add(key);
      fresh.push(location);
    }

    const room = Math.max(0, (typeof maxPicks === "number" ? maxPicks : Infinity) - base.length);
    const added = Math.min(fresh.length, room);

    return {
      locations: [...base, ...fresh.slice(0, added)],
      added,
      // Plain picks carry no group, so there is nothing for a group to adopt.
      absorbed: 0,
      skippedDuplicates,
      skippedOverLimit: fresh.length - added
    };
  };

  /**
   * Picking a whole group CLOSES the dropdown, unlike picking a single area.
   * A group is a finished intent — fifty areas in one tap — so leaving the panel
   * open afterwards invites a second tap on a list whose every row is now
   * partially taken.
   */
  const handlePickGroup = (group: LocationGroup): void => {
    const outcome = mergeGroupIntoSelection(
      locations,
      group,
      typeof maxPicks === "number" ? maxPicks : Infinity
    );

    if (outcome.added > 0 || outcome.absorbed > 0) setLocations(outcome.locations);

    setAnchorEl(null);
    reportMergeOutcome(outcome);
  };

  /** Same reasoning as a group: "all my areas" is one finished intent, so it closes. */
  const handleAddAllFavorites = (): void => {
    const outcome = mergePlainLocations(myAreas);

    if (outcome.added > 0) setLocations(outcome.locations);

    setAnchorEl(null);
    reportMergeOutcome(outcome);
  };

  const handleQuickTabChange = (tab: QuickTab): void => {
    setQuickTab(tab);
    writeStoredQuickTab(tab);
  };

  const inlinePickerWithEdit = usingInlinePicker && props.allowEditResults;

  const showEditButton = inlinePickerWithEdit && locations.length;
  const showClearButton = maxPicks === 1 && locations.length;
  /**
   * A multi-pick picker keeps its query after a pick, so several results can be
   * taken from one search — which leaves the typed text sitting there afterwards
   * with nothing to dismiss it. This clears the TEXT only; the chips below are the
   * selection and are removed one at a time.
   */
  const showClearQueryButton = hasMultipleOptionsEnabled && query.length > 0;

  const cities = suggestions?.filter(
    (row) => row.location.city && !row.location.neighborhood && !row.location.street
  );
  const neighborhoods = suggestions?.filter(
    (row) => row.location.neighborhood && !row.location.street
  );

  const streets = hideStreetResults ? [] : suggestions?.filter((row) => row.location.street);

  const showSuggestionPlaceholder = !cities.length && !neighborhoods.length && !streets.length;

  const isQueryDifferent = query !== getListingAddressString(locations[0]);

  // `>= 2` mirrors the fetch's own gate above (and the server's MIN_QUERY_LENGTH).
  // At `> 2` a two-character search that genuinely found nothing fell through to
  // the "type to search" placeholder instead of saying so.
  const suggestionHasNoResults =
    query.length >= 2 && isQueryDifferent && isEmptySuggestion && showSuggestionPlaceholder;

  const inputLoading = loading || isLoadingSuggestions;

  const customAddress = showEditButton ? locations[0] : null;

  const inputDisabled = disabled || Boolean(customAddress);

  // Before there are two characters to search on there is nothing to show BUT the
  // quick panel, and rendering both would put a stale suggestion list (the fetch is
  // debounced 500ms behind the deleted text) under it.
  const showQuickPanel = hasMultipleOptionsEnabled && !hideQuickTabs && query.length < 2;

  const selectionChips = useMemo(
    () => buildSelectionChips(locations, groups),
    [locations, groups]
  );

  /**
   * The neighborhoods a street runs through, as muted secondary text.
   *
   * Decorative only — it names what the street crosses, it is not part of what
   * picking the street means. A row that DOES carry a neighborhood is an expanded
   * per-neighborhood entry (the `expandStreetNeighborhoods` opt-in, which this
   * picker never requests): there the neighborhood is picked, so repeating it in
   * grey beside itself would read as two different facts.
   */
  const streetHoodsLabel = (row: SuggestionRow): string | undefined => {
    if (row.location.neighborhood) return undefined;

    const names = row.hoodNames ?? [];
    if (!names.length) return undefined;

    const hidden = (row.hoodCount ?? names.length) - names.length;
    const shown = names.join(" · ");

    return hidden > 0
      ? `${shown} ${t("listingsMatch.picker.moreNeighborhoods", { count: hidden })}`
      : shown;
  };

  return (
    <Box>
      <CreateAddressDialog
        open={open}
        address={customAddress}
        autocompleteType={autocompleteType}
        listingInteractiveMapBaseProps={listingInteractiveMapBaseProps}
        onClose={closeModal}
        onConfirm={handleConfirm}
      />
      {/* Sibling of the address dialog, never inside the Popper: closing the popper
          (which opening this does) would otherwise unmount the dialog mid-interaction. */}
      <LocationGroupsManagerDialog
        open={groupsManagerOpen}
        onClose={closeGroupsManagerModal}
        initialEditGroupId={editingGroupId ?? undefined}
        // A chip click means "edit this one", so the rows must not also be pickable —
        // the group is already picked, and a stray tap would re-merge it.
        onPickGroup={
          hasMultipleOptionsEnabled && !editingGroupId ? handlePickGroup : undefined
        }
      />
      <Box ref={fieldRef}>
        <TextField
          fullWidth
          ref={inputRef}
          size={small ? "small" : undefined}
          label={hideLabel ? undefined : t("listingsMatch.picker.label")}
          variant="outlined"
          value={query}
          required={required}
          disabled={inputDisabled}
          InputProps={{
            endAdornment: (
              <>
                {inlineAction && (
                  // Wrapper stops the click from reaching the TextField's
                  // onClick that opens the suggestion popper.
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    {inlineAction}
                  </Box>
                )}
                {!!showEditButton && (
                  <IconButton onClick={preventBubbling(openAddressDialog)}>
                    <Edit />
                  </IconButton>
                )}
                {showClearButton ? (
                  <IconButton onClick={preventBubbling(handleClearLocation)}>
                    <Close />
                  </IconButton>
                ) : inputLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : showClearQueryButton ? (
                  <IconButton size="small" onClick={preventBubbling(handleClearQuery)}>
                    <Close fontSize="small" />
                  </IconButton>
                ) : null}
              </>
            )
          }}
          autoComplete="new-password"
          onChange={handleQueryChange}
          onBlur={handleBlurInput}
          onClick={() => setAnchorEl(fieldRef.current)} // Open Popper when clicking on the field
        />
        {hasMultipleOptionsEnabled && selectionChips.length > 0 && (
          /* A FLEX row, not inline spans.
             `Label` is an inline-flex span that lays out on the text BASELINE, while a
             group chip is a wrapper box of its own — mixing the two in normal inline
             flow left the amber chips sitting a couple of pixels off their green
             neighbours. A flex row with `alignItems: center` aligns both by their box,
             whatever each one is made of, and `gap` replaces the per-chip margins that
             used to double up between two chips of different kinds. */
          <Box
            sx={{
              mt: 1,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.75
            }}
          >
            {/* One chip per plain pick, one per picked GROUP. */}
            {selectionChips.map((chip) =>
              chip.kind === "group" ? (
                <LocationGroupChip
                  key={chip.key}
                  name={chip.name}
                  emoji={chip.emoji}
                  count={chip.locations.length}
                  locations={chip.locations}
                  disabled={disabled}
                  onDelete={handleDeleteChip(chip)}
                  onClick={() => openGroupEditor(chip.groupId)}
                />
              ) : (
                <Label
                  variant="ghost"
                  color="primary"
                  key={chip.key}
                  disabled={disabled}
                  onDelete={handleDeleteChip(chip)}
                >
                  {chip.label}
                </Label>
              )
            )}
          </Box>
        )}
      </Box>
      <Popper
        open={isListOpen}
        style={{ zIndex: 1300 }} // Set Popper width to match input
        anchorEl={anchorEl}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper
            style={{
              width: anchorEl ? anchorEl.clientWidth : "auto", // Match Popper width to input
              backgroundColor: "white"
            }}
            // The scroll lives on the section BELOW, not here, so the quick panel's
            // tab bar and its cog stay pinned inside the same 300px cap.
            //
            // `color` is pinned for the same reason the background is. This surface
            // is forced white in BOTH themes (the suggestion rows below hardcode
            // `#0a6151` on `#d1ffe4`, which only reads on white), but `Paper`'s own
            // root sets `color: text.primary` — which in dark mode is `#fff`. Every
            // descendant that does not name its own colour therefore inherited white
            // ink on a white surface: the quick panel's group names and both empty
            // states were invisible, and the panel read as broken rather than empty.
            // Dropping the white background instead is not the fix — it would leave
            // the hardcoded green rows unreadable on a dark surface.
            sx={{
              maxHeight: 300,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              color: "grey.800"
            }}
            elevation={3} // Add slight shadow for better visibility
          >
            {showQuickPanel ? (
              <QuickPanel
                tab={quickTab}
                onTabChange={handleQuickTabChange}
                favorites={myAreas}
                groups={groups}
                groupsLoading={groupsLoading}
                isHittingLimit={isHittingLimit}
                isLocationPicked={isLocationPicked}
                onPickLocation={handleSelectLocation}
                onAddAllFavorites={handleAddAllFavorites}
                onPickGroup={handlePickGroup}
                onOpenManager={openGroupsManager}
              />
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                {suggestionHasNoResults ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      style={{ padding: "8px 16px", backgroundColor: "#f0f0f0", color: "#333" }}
                    >
                      {t("listingsMatch.picker.noResults")}
                    </Typography>
                    {promptToCreateAddress && (
                      <Button fullWidth onClick={openAddressDialog}>
                        {t("listingsMatch.picker.addAddressManually")}
                      </Button>
                    )}
                  </>
                ) : showSuggestionPlaceholder ? (
                  <Typography
                    variant="subtitle1"
                    style={{ padding: "8px 16px", backgroundColor: "#f0f0f0", color: "#333" }}
                  >
                    {t("listingsMatch.picker.typeToSearch")}
                  </Typography>
                ) : null}
                {cities.length > 0 && (
                  <>
                    <Typography variant="subtitle1" style={sectionHeaderStyle}>
                      {t("listingsMatch.picker.cities")}
                    </Typography>
                    <List>
                      {cities.map((row, index) => {
                        const city = row.location;
                        const selected = isLocationSelected(city);
                        const disabled = isHittingLimit || restrictPickingCities || selected;

                        return (
                          <ListItemButton
                            onClick={
                              restrictPickingCities
                                ? undefined
                                : () => !selected && handleSelectLocation(city)
                            }
                            disabled={disabled}
                            style={rowStyle(selected)}
                            key={index}
                          >
                            <ListItemText primary={getListingAddressString(city)} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                    <Divider />
                  </>
                )}
                {neighborhoods.length > 0 && (
                  <>
                    <Typography variant="subtitle1" style={sectionHeaderStyle}>
                      {t("listingsMatch.picker.neighborhoods")}
                    </Typography>
                    <List>
                      {neighborhoods.map((row, index) => {
                        const neighborhood = row.location;
                        const selected = isLocationSelected(neighborhood);
                        const disabled = isHittingLimit || selected;

                        return (
                          <ListItemButton
                            key={index}
                            onClick={() => !selected && handleSelectLocation(neighborhood)}
                            style={rowStyle(selected)}
                            disabled={disabled}
                          >
                            <ListItemText primary={getListingAddressString(neighborhood)} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                    <Divider />
                  </>
                )}
                {streets.length > 0 && (
                  <>
                    <Typography variant="subtitle1" style={sectionHeaderStyle}>
                      {t("listingsMatch.picker.streets")}
                    </Typography>
                    <List>
                      {streets.map((row, index) => {
                        const street = row.location;
                        const selected = isLocationSelected(street);
                        const disabled = isHittingLimit || selected;

                        return (
                          <ListItemButton
                            onClick={() => !selected && handleSelectLocation(street)}
                            disabled={disabled}
                            style={rowStyle(selected)}
                            key={index}
                          >
                            <ListItemText
                              primary={getListingAddressString(street)}
                              secondary={streetHoodsLabel(row)}
                              secondaryTypographyProps={{
                                variant: "caption",
                                noWrap: true,
                                sx: { color: "grey.600" }
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
}
